<template>
  <div>
    <b-container class="wide">
      <head-block :title="$t('title.customer.products')" />
      <div class="d-flex justify-content-between">

        <search-field
          v-model="searchString"
          @search="getUserProducts"
          :search-placeholder="$t('table.general.search_products')"
        />

        <b-button
          class="mb-2"
          @click="showFilter = !showFilter"
          variant="primary">
          <b-icon icon="filter" scale="1.3"></b-icon>
          {{ $t('table.general.button_filters') }}
        </b-button>
      </div>
      <div v-if="showFilter">
        <products-filter
          @filter-applied="updateProductsWithFilter"
        />
      </div>
      <content-table
        :fields="fields"
        :items="userProducts"
        :slots="templates"
        :primary-key="primaryKey"
        head-variant="dark"
      >
        <template slot="status" slot-scope="data">
          <div class="d-flex justify-content-center">
            <span v-if="editableStatuses.includes(data.tbl.item.status)">
              {{ getObjectByKeyValue(productStatuses, 'code', data.tbl.item.status).name }}
              {{ data.tbl.item.status === statusDraft && (data.tbl.item.name && data.tbl.item.sellPrice) ? ` - ${$t('table.products.status_product_info_filled_in')}` : '' }}
              {{ (!data.tbl.item.name || !data.tbl.item.sellPrice) ? ` - ${$t('table.products.status_product_info_not_filled_in')}` : '' }}
            </span>
            <span v-else>{{ getObjectByKeyValue(productStatuses, 'code', data.tbl.item.status).name }}</span>
          </div>
        </template>
        <template slot="actions" slot-scope="data">
          <b-button
            v-if="soldStatuses.includes(data.tbl.item.status)"
            size="sm"
            v-b-tooltip.hover
            :title="$t('table.products.button_view_product_sales')"
            variant="info"
            :to="{name: 'product-sale-lines', params: { productSheetId: data.tbl.item.productSheet.id, productId: data.tbl.item.id }}">
            <b-icon icon="view-stacked"></b-icon>
          </b-button>
        </template>
      </content-table>
      <span v-if="!loader.isActive && !userProducts.length" class="no-table-data">
        {{ $t('table.general.no_data') }}
      </span>
      <div
        v-if="pagesCount > 1"
        class="pagination_div">
        <paginate
          :value="page"
          :page-count="pagesCount"
          container-class="pagination-container"
          page-class="page-item"
          prev-class="btn btn-primary"
          next-class="btn btn-primary"
          active-class="active-class"
          :click-handler="clickPaginationCallback" />
      </div>
    </b-container>
  </div>
</template>

<script>

  import { mapGetters, mapState } from 'vuex'
  import ContentTable from '@base/components/ContentTable'
  import HeadBlock from '@base/components/HeadBlock.vue'
  import ObjectUtils from '@base/mixins/ObjectUtils.vue'
  import { EDITABLE_STATUSES, SOLD_STATUSES, PRODUCT_DRAFT, PRODUCT_STATUSES } from '@user/configs'
  import SearchField from '@base/components/SearchField.vue'
  import ProductsFilter from '@user/components/ProductsFilter.vue'

  export default {
    name: 'ProductsView',

    components: {
      ProductsFilter,
      SearchField,
      HeadBlock,
      ContentTable,
    },

    mixins: [ ObjectUtils ],

    data() {
      return {
        primaryKey: 'sheetProductId',
        sortBy: 'sheetProductId',
        sortDesc: false,
        fields: [
          {key: 'sheetProductId', label: this.$t('table.products.number'), class: 'text-center number-thead-width'},
          {key: 'name', label: this.$t('table.products.name'), class: 'text-center name-thead-width'},
          {key: 'sellPrice', label: this.$t('table.products.sell_price'), class: 'text-center price-thead-width'},
          {key: 'status', label: this.$t('table.products.status'), class: 'text-center'},
          { key: 'actions', label: this.$t('form.general.actions'), class: 'text-center' }
        ],
        templates: [
          {name: 'status', field: 'status'},
          {name: 'actions', field: 'actions'},
        ],
        loading: false,
        searchString: null,
        filters: {},
        showFilter: false,
        productStatuses: PRODUCT_STATUSES,
        editableStatuses: EDITABLE_STATUSES,
        soldStatuses: SOLD_STATUSES,
        statusDraft: PRODUCT_DRAFT,
      }
    },

    created() {
      this.getUserProducts()
    },

    computed: {
      ...mapState(['userProducts']),
      ...mapGetters({
        page: 'getUserProductsPage',
        pagesCount: 'getUserProductsPagesCount'
      }),
    },

    methods: {

      getUserProducts () {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGE, 1)
        this.$store.commit(this.$_mutationTypes.SET_PRODUCT_FILTERS, { search: this.searchString, ...this.filters })
        this.$store.dispatch(this.$_actionTypes.GET_USER_PRODUCT_LIST)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGES_COUNT, res.totalPages)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      clickPaginationCallback (pageNum) {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)

        this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGE, pageNum)
        this.$store.dispatch(this.$_actionTypes.GET_USER_PRODUCT_LIST)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGES_COUNT, res.totalPages)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      updateProductsWithFilter (filters) {
        this.filters = filters
        this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGE, 1)
        this.getUserProducts()
      },
    }
  }

</script>

<style>

  .number-thead-width {
    min-width: 50px;
    max-width: 70px;
  }
  .name-thead-width {
    max-width: 250px;
  }
  .price-thead-width {
    max-width: 200px;
  }
  .discount-thead-width {
    max-width: 100px;
  }

</style>
